/**
 * Assignments.
 *
 * The obvious way to build this is a checklist: the teacher sets twenty
 * problems, the student ticks them off, the dashboard counts ticks. That is
 * completion-thinking, and it is exactly what this platform exists not to do
 * (§1). A student can answer twenty questions and understand nothing.
 *
 * So an assignment names a **standard to reach**, not an amount of work to get
 * through, and its default target is mastery. A student who already understands
 * the concept is finished immediately — which is correct, and which a checklist
 * cannot express.
 *
 * The second decision follows from the first: **progress is derived, never
 * stored.** There is no submission, no "mark as done", no completion flag to
 * drift out of step with reality. `assignmentProgress` is a pure function of
 * the assignment and the learner's profile, so an assignment completes itself
 * as the student learns, and un-completes if their mastery decays before the
 * due date. That is a feature: forgetting it before Friday means they had not
 * learned it.
 */

import { computeMastery, MASTERY_THRESHOLD, type ConceptState } from './mastery.ts';
import type { LearnerProfile } from './types.ts';

/**
 * What the student has to reach.
 *
 * `mastery` is the default and the one to reach for. `effort` exists because
 * teachers legitimately set practice for its own sake — a warm-up, a revision
 * session before a test — and pretending otherwise would just push them to
 * misuse the mastery target.
 */
export type AssignmentTarget =
  /** Reach this mastery level on every concept. Defaults to the mastery line. */
  | { kind: 'mastery'; threshold?: number }
  /** Answer this many questions per concept, however they go. */
  | { kind: 'effort'; attemptsPerConcept: number }
  /** Read the lesson. For an introduction, before practice makes sense. */
  | { kind: 'lesson' };

export type Assignment = {
  id: string;
  classId: string;
  title: string;
  /** Optional note from the teacher, shown to the student. */
  note?: string;
  conceptIds: string[];
  target: AssignmentTarget;
  /** Epoch milliseconds. Undefined means no deadline. */
  dueAt?: number;
  createdAt: number;
};

export type ConceptProgress = {
  conceptId: string;
  /** 0–1 towards the target, not towards mastery. */
  fraction: number;
  met: boolean;
  /** What the student still has to do, in words. Empty when met. */
  remaining: string;
};

export type AssignmentProgress = {
  assignmentId: string;
  /** Mean progress across the assignment's concepts. */
  fraction: number;
  complete: boolean;
  conceptsMet: number;
  conceptCount: number;
  concepts: ConceptProgress[];
  /** True when the deadline has passed and the target is not met. */
  overdue: boolean;
  /** Milliseconds until due; negative when late. Undefined with no deadline. */
  dueIn: number | undefined;
};

/**
 * How far one student is through one assignment.
 *
 * Pure and derived: nothing here is stored, so it can never disagree with what
 * the student has actually done.
 */
export function assignmentProgress(
  assignment: Assignment,
  profile: LearnerProfile,
  now: number,
): AssignmentProgress {
  const concepts = assignment.conceptIds.map((conceptId) =>
    conceptProgress(assignment.target, conceptId, profile, now),
  );

  const conceptsMet = concepts.filter((entry) => entry.met).length;
  const fraction =
    concepts.length === 0 ? 1 : concepts.reduce((sum, entry) => sum + entry.fraction, 0) / concepts.length;
  const complete = concepts.length > 0 && conceptsMet === concepts.length;

  return {
    assignmentId: assignment.id,
    fraction,
    complete,
    conceptsMet,
    conceptCount: concepts.length,
    concepts,
    overdue: assignment.dueAt !== undefined && now > assignment.dueAt && !complete,
    dueIn: assignment.dueAt === undefined ? undefined : assignment.dueAt - now,
  };
}

function conceptProgress(
  target: AssignmentTarget,
  conceptId: string,
  profile: LearnerProfile,
  now: number,
): ConceptProgress {
  const state: ConceptState | undefined = profile.concepts[conceptId];

  switch (target.kind) {
    case 'lesson': {
      const met = state?.lessonCompleted ?? false;
      return { conceptId, fraction: met ? 1 : 0, met, remaining: met ? '' : 'Read the lesson' };
    }

    case 'effort': {
      const attempts = state?.attempts ?? 0;
      const needed = Math.max(1, target.attemptsPerConcept);
      const met = attempts >= needed;
      return {
        conceptId,
        fraction: Math.min(1, attempts / needed),
        met,
        remaining: met ? '' : `${needed - attempts} more ${needed - attempts === 1 ? 'question' : 'questions'}`,
      };
    }

    case 'mastery': {
      const threshold = target.threshold ?? MASTERY_THRESHOLD;
      const mastery = computeMastery(state, now).overall;
      const met = mastery >= threshold;
      return {
        conceptId,
        // Scaled against the threshold, so a target of 0.6 shows full at 0.6
        // rather than at 1.0 — the bar measures the assignment, not mastery.
        fraction: Math.min(1, threshold === 0 ? 1 : mastery / threshold),
        met,
        remaining: met ? '' : `${Math.round(mastery * 100)}% of ${Math.round(threshold * 100)}%`,
      };
    }
  }
}

/** Human description of the standard, for both teacher and student. */
export function describeTarget(target: AssignmentTarget): string {
  switch (target.kind) {
    case 'lesson':
      return 'Read the lesson';
    case 'effort':
      return `Practise ${target.attemptsPerConcept} questions`;
    case 'mastery': {
      const threshold = target.threshold ?? MASTERY_THRESHOLD;
      return threshold >= MASTERY_THRESHOLD
        ? 'Reach mastery'
        : `Reach ${Math.round(threshold * 100)}% mastery`;
    }
  }
}

/* -------------------------------------------------------------------------- */
/* The class view                                                             */
/* -------------------------------------------------------------------------- */

export type AssignmentCohortRow = {
  profileId: string;
  displayName: string;
  progress: AssignmentProgress;
};

export type AssignmentCohortView = {
  assignment: Assignment;
  completedCount: number;
  overdueCount: number;
  notStartedCount: number;
  averageFraction: number;
  /** Concepts the class is finding hardest within this assignment. */
  hardestConceptIds: string[];
  rows: AssignmentCohortRow[];
};

/**
 * How a class is doing on one assignment.
 *
 * Rows come back sorted by progress ascending — the students who need help are
 * the ones a teacher should see first, not the ones who finished.
 */
export function assignmentCohortView(
  assignment: Assignment,
  members: readonly { profileId: string; displayName: string; profile: LearnerProfile }[],
  now: number,
): AssignmentCohortView {
  const rows: AssignmentCohortRow[] = members
    .map((member) => ({
      profileId: member.profileId,
      displayName: member.displayName,
      progress: assignmentProgress(assignment, member.profile, now),
    }))
    .sort((a, b) => a.progress.fraction - b.progress.fraction);

  // A concept is "hardest" by how many students have not met it, which is the
  // question a teacher is actually asking.
  const unmet = new Map<string, number>();
  for (const row of rows) {
    for (const concept of row.progress.concepts) {
      if (!concept.met) unmet.set(concept.conceptId, (unmet.get(concept.conceptId) ?? 0) + 1);
    }
  }

  return {
    assignment,
    completedCount: rows.filter((row) => row.progress.complete).length,
    overdueCount: rows.filter((row) => row.progress.overdue).length,
    notStartedCount: rows.filter((row) => row.progress.fraction === 0).length,
    averageFraction: rows.length === 0 ? 0 : rows.reduce((sum, row) => sum + row.progress.fraction, 0) / rows.length,
    hardestConceptIds: [...unmet.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([conceptId]) => conceptId),
    rows,
  };
}

/**
 * A student's assignments, most urgent first.
 *
 * Completed work sinks to the bottom rather than disappearing: a student should
 * be able to see what they have finished, and a teacher may set something the
 * student has already met.
 */
export function sortForStudent(
  assignments: readonly Assignment[],
  profile: LearnerProfile,
  now: number,
): { assignment: Assignment; progress: AssignmentProgress }[] {
  return assignments
    .map((assignment) => ({ assignment, progress: assignmentProgress(assignment, profile, now) }))
    .sort((a, b) => {
      if (a.progress.complete !== b.progress.complete) return a.progress.complete ? 1 : -1;
      if (a.progress.overdue !== b.progress.overdue) return a.progress.overdue ? -1 : 1;
      // Then by deadline; undated work comes after dated work.
      const aDue = a.assignment.dueAt ?? Number.POSITIVE_INFINITY;
      const bDue = b.assignment.dueAt ?? Number.POSITIVE_INFINITY;
      return aDue - bDue;
    });
}
